import React, {useState} from 'react'
import {useMutation} from 'react-apollo'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import {AlertDialog} from '../../../Common/Dialog'
import {TauroTermsCheckbox, DEFAULT_TAURO_MULTIPLIER} from '../../../Common/TauroTermsCheckbox'
import {TauroRulesModal} from '../../../Common/TauroRulesModal'
import messages from '../../../../assets/messages'
import {CONFIRM_PARTNER_LEGAL_ACCEPTANCE_MUTATION} from '../../../../graphql/mutations'
import {CLIENT_DATA_QUERY} from '../../../../graphql/queries'
import {config} from '../../../../config'
import {getViewerTauroFactorisation} from '../../../Accounts/AddAccount/tauroTerms'

interface OwnProps {
  open: boolean
  viewer?: {
    requiresPartnerCashbackDisclaimer?: boolean
  }
  onCancel: () => void
}

type Props = OwnProps & WithNamespaces

const renderLink = (label: string, href: string | undefined) => {
  if (!href) {
    return <strong>{label}</strong>
  }
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </Link>
  )
}

const SolicitationAcceptanceBannerComponent: React.FC<Props> = ({open, onCancel, t, viewer}) => {
  const [submitting, setSubmitting] = useState(false)
  const [tauroTermsAccepted, setTauroTermsAccepted] = useState(false)
  const [cashbackDisclaimerAccepted, setCashbackDisclaimerAccepted] = useState(false)
  const [tauroRulesOpen, setTauroRulesOpen] = useState(false)
  const [confirmLegal, {error}] = useMutation(CONFIRM_PARTNER_LEGAL_ACCEPTANCE_MUTATION, {
    refetchQueries: [{query: CLIENT_DATA_QUERY}],
    awaitRefetchQueries: true,
  })

  const links = (config.legalLinks || {}) as {
    terms?: string
    legal?: string
    privacy?: string
  }
  const tauroRules = getViewerTauroFactorisation(viewer)
  const tauroRequired = tauroRules != null
  const tauroMultiplier = tauroRules?.multiplier ?? DEFAULT_TAURO_MULTIPLIER
  const cashbackRequired = viewer?.requiresPartnerCashbackDisclaimer === true

  const handleConfirm = async () => {
    if (tauroRequired && !tauroTermsAccepted) return
    if (cashbackRequired && !cashbackDisclaimerAccepted) return
    setSubmitting(true)
    try {
      await confirmLegal({
        variables: {
          ...(tauroRequired ? {tauroTermsAccepted: true} : {}),
          ...(cashbackRequired ? {cashbackDisclaimerAcceptance: true} : {}),
        },
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <AlertDialog
        open={open}
        title={t(messages.solicitationAcceptanceTitle.i18nKey, messages.solicitationAcceptanceTitle.defaults)}
        onAgree={handleConfirm}
        onDisagree={onCancel}
        onClose={onCancel}
        agreeText={t(messages.confirm.i18nKey, messages.confirm.defaults)}
        disagreeText={t(messages.cancel.i18nKey, messages.cancel.defaults)}
        buttonLoading={submitting}
        buttonDisabled={
          (tauroRequired && !tauroTermsAccepted) || (cashbackRequired && !cashbackDisclaimerAccepted)
        }
        errorText={error ? error.message : undefined}
        maxWidth="sm"
      >
        <Typography variant="body1" gutterBottom>
          <Trans
            {...messages.solicitationAcceptancePart1}
            components={[
              <a href="https://tauromarkets.com" target="_blank" rel="noreferrer noopener" />,
            ]}
          />
        </Typography>
        <Typography variant="body1" gutterBottom>
          <Trans {...messages.solicitationAcceptancePart2} />
        </Typography>
        <Typography variant="body1" gutterBottom>
          <Trans {...messages.solicitationAcceptancePart3} />
        </Typography>
        <Typography variant="body1" gutterBottom>
          <Trans {...messages.partnerLegalConsentIntro} />
        </Typography>
        <ul>
          <li>{renderLink(t(messages.partnerLegalConsentTerms.i18nKey, messages.partnerLegalConsentTerms.defaults), links.terms)}</li>
          <li>{renderLink(t(messages.partnerLegalConsentLegal.i18nKey, messages.partnerLegalConsentLegal.defaults), links.legal)}</li>
          <li>{renderLink(t(messages.partnerLegalConsentPrivacy.i18nKey, messages.partnerLegalConsentPrivacy.defaults), links.privacy)}</li>
        </ul>
        {cashbackRequired && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Partner Cashback &amp; Commission Disclaimer
            </Typography>
            <Typography variant="body2" paragraph>
              <em>
                The Client hereby acknowledges and agrees that any cashback, rebates, or commission distributions
                received from or facilitated by their assigned Partner (IB) are agreed upon independently with said
                Partner. The Client explicitly agrees that Tauro Markets assumes no responsibility, liability, or
                obligation for the calculation, accuracy, frequency, or execution of such cashback distributions or
                amounts.
              </em>
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={cashbackDisclaimerAccepted}
                  onChange={(e) => setCashbackDisclaimerAccepted(e.target.checked)}
                />
              }
              label={<Trans {...messages.partnerCashbackDisclaimerAccept} />}
            />
          </>
        )}
        {tauroRequired && (
          <>
            <Typography variant="caption" paragraph>
              <Trans {...messages.tauroTermsDescription} values={{multiplier: tauroMultiplier}} />
            </Typography>
            <TauroTermsCheckbox
              checked={tauroTermsAccepted}
              multiplier={tauroMultiplier}
              onChange={setTauroTermsAccepted}
              onOpenRules={() => setTauroRulesOpen(true)}
            />
          </>
        )}
      </AlertDialog>
      <TauroRulesModal open={tauroRulesOpen} onClose={() => setTauroRulesOpen(false)} rules={tauroRules} />
    </>
  )
}

export const SolicitationAcceptanceBanner = withNamespaces()(SolicitationAcceptanceBannerComponent) as unknown as React.FC<OwnProps>
